import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Accessibility,
  X,
  Keyboard,
  Pause,
  Contrast,
  ZoomIn,
  ZoomOut,
  Type,
  Heading,
  Link2,
  RotateCcw,
  Check,
} from "lucide-react";

interface AccessibilitySettings {
  keyboardNav: boolean;
  noAnimations: boolean;
  highContrast: boolean;
  largeText: boolean;
  smallText: boolean;
  readableFont: boolean;
  highlightHeadings: boolean;
  highlightLinks: boolean;
}

const defaultSettings: AccessibilitySettings = {
  keyboardNav: false,
  noAnimations: false,
  highContrast: false,
  largeText: false,
  smallText: false,
  readableFont: false,
  highlightHeadings: false,
  highlightLinks: false,
};

const STORAGE_KEY = "adam-safety-accessibility";

export function AccessibilityWidget() {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);

  // Load settings from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings(parsed);
        applySettings(parsed);
      } catch (e) {
        console.error("Failed to parse accessibility settings", e);
      }
    }
  }, []);

  // Apply CSS classes to document
  const applySettings = (newSettings: AccessibilitySettings) => {
    const html = document.documentElement;
    
    html.classList.toggle("a11y-keyboard-nav", newSettings.keyboardNav);
    html.classList.toggle("a11y-no-animations", newSettings.noAnimations);
    html.classList.toggle("a11y-high-contrast", newSettings.highContrast);
    html.classList.toggle("a11y-large-text", newSettings.largeText);
    html.classList.toggle("a11y-small-text", newSettings.smallText);
    html.classList.toggle("a11y-readable-font", newSettings.readableFont);
    html.classList.toggle("a11y-highlight-headings", newSettings.highlightHeadings);
    html.classList.toggle("a11y-highlight-links", newSettings.highlightLinks);
  };

  const updateSetting = (key: keyof AccessibilitySettings) => {
    // Handle mutually exclusive settings
    let newSettings = { ...settings };
    
    if (key === "largeText" && !settings.largeText) {
      newSettings.smallText = false;
    } else if (key === "smallText" && !settings.smallText) {
      newSettings.largeText = false;
    }
    
    newSettings[key] = !newSettings[key];
    
    setSettings(newSettings);
    applySettings(newSettings);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    applySettings(defaultSettings);
    localStorage.removeItem(STORAGE_KEY);
  };

  const options = [
    { key: "keyboardNav" as const, icon: Keyboard, label: t("accessibility.keyboardNav") },
    { key: "noAnimations" as const, icon: Pause, label: t("accessibility.disableAnimations") },
    { key: "highContrast" as const, icon: Contrast, label: t("accessibility.highContrast") },
    { key: "largeText" as const, icon: ZoomIn, label: t("accessibility.increaseText") },
    { key: "smallText" as const, icon: ZoomOut, label: t("accessibility.decreaseText") },
    { key: "readableFont" as const, icon: Type, label: t("accessibility.readableFont") },
    { key: "highlightHeadings" as const, icon: Heading, label: t("accessibility.highlightHeadings") },
    { key: "highlightLinks" as const, icon: Link2, label: t("accessibility.highlightLinks") },
  ];

  return (
    <>
      {/* Main Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed z-50 w-12 h-12 md:w-14 md:h-14 bg-accent rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
        style={{ 
          bottom: 'max(env(safe-area-inset-bottom, 0px) + 16px, 24px)',
          right: '16px'
        }}
        aria-label={t("accessibility.title")}
        aria-expanded={isOpen}
      >
        <Accessibility className="w-6 h-6 md:w-7 md:h-7 text-accent-foreground" />
      </button>

      {/* Menu Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
            />
            
            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="fixed z-50 w-80 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
              style={{ 
                bottom: 'max(env(safe-area-inset-bottom, 0px) + 80px, 96px)',
                right: '16px'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border bg-muted/50">
                <div className="flex items-center gap-2">
                  <Accessibility className="w-5 h-5 text-accent" />
                  <h3 className="font-semibold text-foreground">{t("accessibility.title")}</h3>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-muted rounded-full transition-colors"
                  aria-label={t("buttons.close")}
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {/* Options Grid */}
              <div className="p-4 grid grid-cols-2 gap-3">
                {options.map(({ key, icon: Icon, label }) => (
                  <button
                    key={key}
                    onClick={() => updateSetting(key)}
                    className={`
                      relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl
                      border-2 transition-all duration-200
                      ${settings[key]
                        ? "border-accent bg-accent/10 text-accent"
                        : "border-border bg-background hover:border-accent/50 hover:bg-muted/50 text-foreground"
                      }
                    `}
                    aria-pressed={settings[key]}
                  >
                    {settings[key] && (
                      <div className="absolute top-2 right-2">
                        <Check className="w-4 h-4 text-accent" />
                      </div>
                    )}
                    <Icon className="w-6 h-6" />
                    <span className="text-xs font-medium text-center leading-tight">{label}</span>
                  </button>
                ))}
              </div>

              {/* Reset Button */}
              <div className="p-4 pt-0">
                <button
                  onClick={resetSettings}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-destructive/30 bg-destructive/5 hover:bg-destructive/10 text-destructive transition-all duration-200"
                >
                  <RotateCcw className="w-5 h-5" />
                  <span className="font-medium">{t("accessibility.reset")}</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
